import React, { useEffect } from 'react';
import mixpanel from 'mixpanel-browser';
import { t } from 'i18next';
import axios from 'axios';
import { FontWeight, Heading, Text } from '@lili/lili-frontend-common';
import { BASE_URL } from '../../constants/endpoints';
import { GreyBgCard } from './components/Card';
import { TextWithCopy } from './components/TextWithCopy';
import achIcon from '../../assets/icons/svg/colored-icons/ic-colored-ach-icon.svg';

import { EmailSection } from './components/EmailSection';
import { ProviderCard } from './components/ProviderCard';
import { useStore } from '../../store';
import { getAddressObj } from '../../helpers/address.helpers';
import { handleDownload } from './utils';
import { LiliCard } from './components/LiliCard';
import PageLayout from '../../generic-components/PageLayout/PageLayout';
import PageHeader from '../../generic-components/PageHeader/PageHeader';

export const AchTransfer = () => {
  const HEADER = t('DIRECT_DEPOSIT_PAGE_HEADER');
  const { getPrimaryAccount, getPersonalAccountId } = useStore((state) => state);
  const { customerId } = useStore((state) => state.currUser());
  const [isEmailSent, setIsEmailSent] = React.useState(false);
  const [isDownloading, setIsDownloading] = React.useState(false);

  const handleDownloadClick = async () => {
    setIsDownloading(true);
    const extId = getPersonalAccountId();
    const url = `${BASE_URL}/api/v1/user/${customerId}/${extId}/confirmation/ach/bankAccount/download`;

    try {
      const { data } = await axios(url, {
        responseType: 'blob',
        method: 'GET',
      });
      handleDownload(URL.createObjectURL(data), 'Lili Banking Domestic ACH Transfer Instructions.pdf');
      setIsDownloading(false);
    } catch (error) {
      console.error(error);
      setIsDownloading(false);
    }
  };

  const {
    achAccountNumber,
    routingNumber,
    bankInfo: { bank, bankInstAddress, bankInstInfo, bankInstName },
  } = getPrimaryAccount();
  const disclaimer = {
    title: t('INT_WIRE_DISCLAIMER_1'),
    description: t('DIRECT_DEPOSIT_PAGE_FOOTNOTE'),
  };
  const providerHeadline = t('INT_BANKING_SERVICE');
  const splitBankAddress = getAddressObj(bank, bankInstAddress);

  const providerLines = [
    {
      header: bankInstName,
      line1: bankInstInfo,
      line2: splitBankAddress.addressLine,
      line3: splitBankAddress.stateAndZip,
    },
  ];

  const CardSubContentHeader = t('DIRECT_DEPOSIT_PAGE_SUB_CONTENT_HEADER');
  const greyBgCardTitle = t('DIRECT_DEPOSIT_PAGE_SUBHEADER');

  useEffect(() => {
    mixpanel.track('View Set Up ACH Transfer Screen');
    mixpanel.track('Act on Add Money Screen', { Action: 'Set Up Direct Deposit', ui_screen: 'Menu Screen' });
  }, []);

  return (
    <PageLayout>
      <PageHeader title={HEADER} showBackButton={false} />
      <div className='flex flex-row gap-[24px] h-[78dvh]'>
        <LiliCard className='basis-[45%] h-full w-full p-[32px] flex flex-col justify-between'>
          <div>
            <Heading.H9 fontWeight={FontWeight.MEDIUM} id='yourAccountDetails' className='mb-[24px]'>
              {CardSubContentHeader}
            </Heading.H9>
            <TextWithCopy title={t('DIRECT_DEPOSIT_PAGE_ACC_NUM')} text={achAccountNumber} />
            <TextWithCopy title={t('DIRECT_DEPOSIT_PAGE_ROUT_NUM')} text={routingNumber ?? ''} />
          </div>
          <EmailSection
            setIsEmailSent={setIsEmailSent}
            handleSendEmail={handleDownloadClick}
            isEmailSent={isEmailSent}
            isLoading={isDownloading}
            isRefetching={isDownloading}
            title={t('DIRECT_DEPOSIT_PAGE_DOWNLOAD')}
          />
        </LiliCard>
        <LiliCard className='basis-[55%] w-full flex flex-col p-[32px] gap-[24px]'>
          <GreyBgCard
            iconId='calendar'
            iconSrc={achIcon}
            text={
              <div>
                <div>
                  {t('DIRECT_DEPOSIT_PAGE_TEXT')}
                  <Text.H10 fontWeight={FontWeight.SEMIBOLD} className='inline'>
                    {t('DIRECT_DEPOSIT_PAGE_TEXT_2')}
                  </Text.H10>
                </div>
                {t('DIRECT_DEPOSIT_PAGE_TEXT_3')}
              </div>
            }
            title={greyBgCardTitle}
          />
          <ProviderCard providerHeadline={providerHeadline} disclaimer={disclaimer} providersLines={providerLines} />
        </LiliCard>
      </div>
    </PageLayout>
  );
};
